import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTimes, 
  faCheck, 
  faUserCheck,
  faUserTimes,
  faSpinner
} from '@fortawesome/free-solid-svg-icons';
import './StudentSelectionModal.css';

const StudentSelectionModal = ({ 
  isOpen, 
  onClose, 
  student, 
  companyId,
  onSelectionUpdate 
}) => {
  const [selectionData, setSelectionData] = useState({
    isSelected: null,
    selectionFeedback: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Reset form when modal opens
  React.useEffect(() => {
    if (isOpen && student) {
      setSelectionData({
        isSelected: student.isSelected !== null ? student.isSelected : null,
        selectionFeedback: student.selectionFeedback || ''
      });
      setError('');
    }
  }, [isOpen, student]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (selectionData.isSelected === null) {
      setError('Please select whether the student is selected or not');
      return;
    }

    if (!selectionData.selectionFeedback.trim()) {
      setError('Please provide feedback for the selection decision');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/placement/${companyId}/selection`, 
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            studentId: student.studentId,
            isSelected: selectionData.isSelected,
            selectionFeedback: selectionData.selectionFeedback
          }),
        }
      );

      if (response.ok) {
        const result = await response.json();
        onSelectionUpdate(student.studentId, {
          isSelected: selectionData.isSelected,
          selectionFeedback: selectionData.selectionFeedback
        });
        onClose();
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to update selection status');
      }
    } catch (error) {
      console.error('Error updating selection:', error);
      setError('Network error. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSelectionChange = (isSelected) => {
    setSelectionData(prev => ({
      ...prev,
      isSelected: isSelected
    }));
  };

  if (!isOpen || !student) return null;

  return (
    <div className="selection-modal-overlay" onClick={onClose}>
      <div className="selection-modal" onClick={(e) => e.stopPropagation()}>
        <div className="selection-modal-header">
          <h3>
            <FontAwesomeIcon icon={faUserCheck} />
            Update Selection Status
          </h3>
          <button 
            className="close-modal-btn"
            onClick={onClose}
            disabled={isSubmitting}
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="selection-modal-body">
          <div className="student-info-section">
            <h4>Student Information</h4>
            <div className="student-details-grid">
              <div className="detail-item">
                <span className="detail-label">Student ID:</span>
                <span className="detail-value">{student.studentId}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Name:</span>
                <span className="detail-value">{student.studentName}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Branch:</span>
                <span className="detail-value">{student.branch}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Interview Status:</span>
                <span className="detail-value status-attended">Attended</span>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="selection-form">
            <div className="form-group">
              <label className="form-label">Selection Decision*</label>
              <div className="selection-buttons">
                <button
                  type="button"
                  className={`selection-btn selected-btn ${selectionData.isSelected === true ? 'active' : ''}`}
                  onClick={() => handleSelectionChange(true)}
                  disabled={isSubmitting}
                >
                  <FontAwesomeIcon icon={faUserCheck} />
                  Selected
                </button>
                <button
                  type="button"
                  className={`selection-btn not-selected-btn ${selectionData.isSelected === false ? 'active' : ''}`}
                  onClick={() => handleSelectionChange(false)}
                  disabled={isSubmitting}
                >
                  <FontAwesomeIcon icon={faUserTimes} />
                  Not Selected
                </button>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="selectionFeedback" className="form-label">
                Selection Feedback*
              </label>
              <textarea
                id="selectionFeedback"
                value={selectionData.selectionFeedback}
                onChange={(e) => setSelectionData(prev => ({
                  ...prev,
                  selectionFeedback: e.target.value
                }))}
                placeholder="Please provide detailed feedback about the selection decision..."
                rows={4}
                disabled={isSubmitting}
                required
              />
            </div>

            {error && (
              <div className="error-message">
                <FontAwesomeIcon icon={faTimes} />
                {error}
              </div>
            )}

            <div className="form-actions">
              <button
                type="button"
                className="cancel-btn"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="submit-btn"
                disabled={isSubmitting || selectionData.isSelected === null || !selectionData.selectionFeedback.trim()}
              >
                {isSubmitting ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin />
                    Updating...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faCheck} />
                    Update Selection
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default StudentSelectionModal;